import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  ArrowRightLeft,
} from 'lucide-react';
import { mojaloopApi } from '../services/api';
import Pagination from '../components/Pagination';
import { formatDate, debounce } from '../utils/helpers';

const STATUS_BADGE = {
  COMMITTED: 'badge-success',
  RESERVED:  'bg-blue-100 text-blue-800',
  ABORTED:   'badge-danger',
  EXPIRED:   'badge-warning',
  PENDING:   'badge-info',
  ERROR:     'badge-danger',
};

const Transfers = () => {
  const [transfers, setTransfers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filters, setFilters]       = useState({ status: '', search: '' });
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadTransfers = useCallback(async (pg = page, sz = pageSize) => {
    try {
      setLoading(true);
      const data = await mojaloopApi.getTransfers({
        status: filters.status,
        search: filters.search,
        page: pg,
        size: sz,
      });
      console.log('[Transfers] raw page response:', data);
      console.log('[Transfers] first record sample:', data.content?.[0]);
      setTransfers(data.content    ?? []);
      setTotalItems(data.totalItems ?? 0);
      setTotalPages(data.totalPages ?? 0);
      setPage(data.page ?? 0);
    } catch (err) {
      console.error('[Transfers] load error:', err);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(0); loadTransfers(0, pageSize); }, [filters]); // eslint-disable-line
  useEffect(() => { loadTransfers(page, pageSize); }, [page, pageSize]);   // eslint-disable-line

  const handleSearchChange = debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
  }, 300);

  const handlePageChange     = (p) => setPage(p - 1);
  const handlePageSizeChange = (s) => { setPageSize(s); setPage(0); };
  const clearFilters         = () => setFilters({ status: '', search: '' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Transfers</h1>
          <p className="text-gray-600 mt-1">Mojaloop FSPIOP transfer records</p>
        </div>
        <button onClick={() => loadTransfers(page, pageSize)} disabled={loading} className="btn-primary flex items-center space-x-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Transfer ID, E2E ID, Payer FSP, Payee FSP…"
                  className="input pl-10"
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="COMMITTED">Committed</option>
                <option value="RESERVED">Reserved</option>
                <option value="ABORTED">Aborted</option>
                <option value="EXPIRED">Expired</option>
                <option value="PENDING">Pending</option>
                <option value="ERROR">Error</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Results{totalItems > 0 ? ` (${totalItems.toLocaleString()})` : ''}
          </h2>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading transfers…</p>
              </div>
            </div>
          ) : transfers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <ArrowRightLeft className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No transfers found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ID', 'Transfer ID', 'End-to-End ID', 'Payer FSP', 'Payee FSP', 'Amount', 'Status', 'Created', ''].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transfers.map((tx) => (
                      <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                          #{tx.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          <span title={tx.transferId}>{tx.transferId?.substring(0, 16)}…</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                          {tx.endToEndId || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{tx.payerFsp}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{tx.payeeFsp}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {tx.amount} <span className="text-gray-400 text-xs">{tx.currency}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`badge ${STATUS_BADGE[tx.status] || 'badge-info'}`}>
                            {tx.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(tx.createdAt, 'PPp')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/transfers/${tx.id}`}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center space-x-1"
                          >
                            <span>View</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page + 1}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transfers;
